/**
 * ID58: 미리보기 URL 조립용 스토어프론트 origin.
 *
 * ⚠ 계약(00-CONTRACT.md §ID58): BE 가 반환하는 `url` 의 host 는 dev 기본 `localhost:3000`.
 * BO 는 그 host 를 신뢰하지 않고, 응답의 `token` + 이 origin 으로 최종 링크를 직접 구성한다.
 *
 * 우선순위:
 *  1. 백오피스 자신의 호스트(qa-office → QA FE, office → 운영 FE). 알려진 배포 호스트면 이것이 정답이다.
 *  2. `VITE_SF_ORIGIN` 환경변수(로컬·기타 호스트용 override).
 *  3. dev 기본값 `https://ever-dev.twomos.com`.
 *
 * 호스트를 env 보다 앞에 두는 이유: QA·운영 이미지는 GitHub(ecr-push) 외 파이프라인으로도 빌드되고, 그쪽
 * 빌드 인자가 비거나 다른 환경 값이면 QA 백오피스 상품 링크가 dev FE(ever-dev)로 나갔다(재발 포함). 같은
 * 이유로 커밋된 .env.production(QA 값)이 운영 이미지에 실려도 운영 링크가 QA 로 나가지 않는다.
 *
 * 후행 슬래시는 제거해 `${origin}/products/preview?token=...` 조립 시 중복 슬래시를 피한다.
 */
const DEV_DEFAULT_SF_ORIGIN = 'https://ever-dev.twomos.com';

/** 백오피스 호스트 → 같은 환경의 쇼핑몰 origin. */
const SF_ORIGIN_BY_OFFICE_HOST: Readonly<Record<string, string>> = {
  'qa-office.everlineshop.com': 'https://qa.everlineshop.com',
  // 2026-10-21 운영 도메인 전환 — 운영 쇼핑몰은 everlineshop.com(www 없음, 운영 결정). 관리자 도메인(prd-office / office)은 그대로.
  'prd-office.everlineshop.com': 'https://everlineshop.com',
  'office.everlineshop.com': 'https://everlineshop.com',
};

const stripTrailingSlash = (s: string): string => s.replace(/\/+$/, '');

const originForCurrentHost = (): string | undefined =>
  typeof window === 'undefined' ? undefined : SF_ORIGIN_BY_OFFICE_HOST[window.location.hostname];

export const getStorefrontOrigin = (): string => {
  const fromHost = originForCurrentHost();
  if (fromHost) return fromHost;
  const fromEnv = import.meta.env.VITE_SF_ORIGIN as string | undefined;
  const origin = fromEnv && fromEnv.trim() !== '' ? fromEnv.trim() : DEV_DEFAULT_SF_ORIGIN;
  return stripTrailingSlash(origin);
};

export type PreviewContentType = 'PRODUCT' | 'EVENT' | 'POPUP';

const PREVIEW_PATH: Record<PreviewContentType, string> = {
  PRODUCT: '/products/preview',
  EVENT: '/events/preview',
  POPUP: '/popups/preview',
};

/** token + contentType → 스토어프론트 미리보기 최종 URL. BE url host 미사용. */
export const buildPreviewUrl = (contentType: PreviewContentType, token: string): string =>
  `${getStorefrontOrigin()}${PREVIEW_PATH[contentType]}?token=${encodeURIComponent(token)}`;
